import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { api, type DashboardCard } from '../lib/api';
import { Card, CardHeader, MetricCard, Badge, ActionButton, Spinner, EmptyState, Table, Th, Tr, Td } from '../components/ui';
import { fmtCurrencyWhole, fmtPct } from '../lib/fmt';

function display(card: DashboardCard): string {
  if (card.placeholder) return card.placeholder;
  const v = card.value;
  if (v == null) return '—';
  switch (card.format) {
    case 'currency': return fmtCurrencyWhole(Number(v));
    case 'percent': return fmtPct(Number(v));
    case 'days': return `${Math.round(Number(v))} days`;
    case 'months': return `${v} mo`;
    case 'ratio': return `${v}×`;
    default: return String(v);
  }
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery(() => api.dashboards.me());
  const [customize, setCustomize] = useState(false);

  async function toggle(card: DashboardCard) {
    await api.dashboards.update([{ metric_code: card.code, hidden: !card.hidden }]);
    refetch();
  }

  const cards = data?.cards ?? [];
  const visible = cards.filter((c) => !c.hidden);

  return (
    <>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Executive dashboard</div>
          <span style={{ marginLeft: 'auto' }}>
            <ActionButton onClick={() => setCustomize((v) => !v)}>{customize ? 'Done' : 'Customize'}</ActionButton>
          </span>
        </CardHeader>
        {isLoading ? <Spinner />
          : error ? <EmptyState message={error} />
          : visible.length === 0 ? <EmptyState message="No metrics selected — use Customize to add some." />
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 16 }}>
              {visible.map((c) => (
                <div key={c.code} style={{ width: 240 }}>
                  <MetricCard
                    label={c.display_name}
                    value={c.placeholder ? <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{c.placeholder}</span> : display(c)}
                    sub={c.sub ?? (c.requires_fpa_role ? 'financial' : undefined)}
                    onClick={c.drilldown_target && !c.placeholder ? () => navigate(c.drilldown_target!) : undefined}
                  />
                </div>
              ))}
            </div>
          )}
      </Card>

      {customize && (
        <Card>
          <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>Customize metrics</div></CardHeader>
          <Table>
            <thead><tr><Th>Metric</Th><Th>Type</Th><Th> </Th></tr></thead>
            <tbody>
              {cards.map((c) => (
                <Tr key={c.code}>
                  <Td bold>{c.display_name}</Td>
                  <Td>{c.requires_fpa_role ? <Badge variant="blue">financial</Badge> : <Badge variant="gray">operational</Badge>}</Td>
                  <Td><ActionButton onClick={() => toggle(c)}>{c.hidden ? 'Show' : 'Hide'}</ActionButton></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}
